import { useInfiniteQuery } from '@tanstack/react-query'
import { useOrderItemsService } from '@/flight-deck-api/services/order-items.service'
import { useSession } from '@/providers/session'
import type { TransformedOrderItemFilters } from './use-transformed-order-item-filters'

export const useGetOrderItemsInfinite = ({
  status,
  search,
  order,
  orderBy,
  limit = 10,
  offset = 0,
  'genre-ids': genreIds,
  'network-ids': networkIds,
  'offering-types': offeringTypes,
  'cpm-min': cpmMin,
  'cpm-max': cpmMax,
  'air-dates-start': airDatesStart,
  'air-dates-end': airDatesEnd,
}: {
  status?: 'upcoming' | 'live' | 'completed'
  search?: string
  order?: 'asc' | 'desc'
  orderBy?: 'expiration' | 'name' | 'search' | 'created'
  limit?: number
  offset?: number
} & TransformedOrderItemFilters) => {
  const { getOrderItems } = useOrderItemsService()
  const { isLoaded: isSessionLoaded } = useSession()

  const {
    data,
    error,
    status: fetchStatus,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [
      'orderItems',
      status,
      search,
      order,
      orderBy,
      genreIds,
      networkIds,
      offeringTypes,
      cpmMin,
      cpmMax,
      airDatesStart,
      airDatesEnd,
    ],
    queryFn: async ({ pageParam }) => {
      const offset = pageParam * limit

      // biome-ignore lint/plugin: RETIRED, do not copy. Call it through useSelectQuery/useSelectMutation when touching this.
      return await getOrderItems.handler({
        status,
        search,
        order,
        'order-by': orderBy,
        limit,
        offset,
        'genre-ids': genreIds,
        'network-ids': networkIds,
        'offering-types': offeringTypes,
        'cpm-min': cpmMin,
        'cpm-max': cpmMax,
        'air-dates-start': airDatesStart,
        'air-dates-end': airDatesEnd,
      })
    },
    initialPageParam: 0,
    getNextPageParam: ({ data }, _, prevPageParam) => {
      return data.length < limit ? undefined : prevPageParam + 1
    },
    enabled: isSessionLoaded,
    refetchOnWindowFocus: true, // Refetch when returning to the dashboard
    refetchOnMount: 'always', // Always refetch when component mounts to get fresh campaign data
  })

  const orderItems = data?.pages.flatMap(page => page.data) || []

  const page = data?.pages[0]?.page || {
    limit,
    offset,
    totalCount: 0,
    totalPages: 0,
    currentPage: 0,
  }

  return {
    orderItems,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    page,
    status: fetchStatus,
    error,
  }
}
